<template>
  <tile class="opc-addon-config">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i v-if="hasSelectedPlan && !isAdd2CartOrEditFlow" check-icon class="zm-icon-success main-icon" aria-hidden="true" />
          </h2>
          <div v-if="!isExpanded" slot="collapse" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <div v-if="hasSelectedPlan">
              <ul>
                <li>{{ cartSelectedPlan.cartInfo.selected_number }} {{ $t('billing.planform.unit.license') }}</li>
                <li>
                  <span>
                    {{ $money(cartSelectedPlan.amount) }}{{
                      $t(`billing.common.per_billing_cycle.${cartSelectedPlan.billing_cycle}`) }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="opc-addon-config__main">
        <p v-if="isAdd2CartFlow || isEditFlow" class="opc-addon-config__main__title" @click="onTipsLinkClick"
          v-html="$t('billing.translation_caption.subtitle')"></p>
        <p v-else class="opc-addon-config__main__title">{{ configDescription }}</p>
        <div class="opc-addon-config__main__config">
          <div class="opc-addon-config-row__input__number">
            <input-number-with-popover :value="license" ref="userInput" @change="changeLicense" size="large"
              controls-position="both" :min="minHosts" :max="maxHosts"
              :decrease-tips="decreaseTips" :label="title + ' Select the number of licenses'" />
            <span class="opc-addon-config-row__input__number--unit">{{ $t('billing.planform.unit.license') }}</span>
            <bought-license :type="planTypes.TRANSLATION_CAPTIONS" />
          </div>
          <zm-alert v-if="mx_downSellLicenseErrorMessage" class="opc-common-info-alert" type="warning" :closable="false"
            show-icon>
            <p slot="title" v-html="mx_downSellLicenseErrorMessage"></p>
          </zm-alert>
          <subscription-cycle :value="selectedPlanCode" :options="billingCycleOptions" @change="onPlanSelect" />
        </div>
      </div>
      <div v-if="isShowRemoveBtn" class="opc-zm-base-card__main__footer">
        <div class="opc-addon-config__btn--wrap">
          <zm-button type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + title + ' plan'">{{
            $t('billing.common.remove') }}</zm-button>
        </div>
      </div>
      <preview-payment class="opc-addon-config__preview-payment" />
    </foldable>
  </tile>
</template>

<script>
import plansMixin from '@/plans/mx/plan';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import {
  planTypes,
  trackingPlanTypes,
  BILLING_CYCLE,
  DEFAULT_MAX_HOST,
  DEFAULT_MIN_HOST,
  BillingCycleEnum,
} from '@/common/constants';
import { flowTypes } from '@/common/service/page-config.js';
import _, { get } from 'lodash';
import AddonMixin from '../mx/addon';
import SubscriptionCycle from '@/components/subscription-cycle.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';

export default {
  name: 'Translation-Config',
  mixins: [plansMixin, AddonMixin],
  components: { PreviewPayment, Tile, Foldable, InputNumberWithPopover, SubscriptionCycle, BoughtLicense},
  data() {
    return {
      isExpanded: true,
      selectedPlanCode: '',
      license: 1,
      title: this.$t('billing.translation_caption.title'),
      planType: planTypes.TRANSLATION_CAPTIONS,
      previousQuantity: 1,
      configDescription: this.$t('billing.translation_caption.config_desc'),
      planTypes,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getCurrentBasePlanInfo', 'basicConfig', 'currentEditPlan']),
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
    }),
    logo() {
      return this.isAdd2CartFlow || this.isEditFlow ? require('@/assets/plans/translated_captions_add.svg') : require('@/assets/plans/translated_captions.svg')
    },
    isAdd2CartFlow() {
      return this.$context.flow === flowTypes.add2cart && this.$context.is.addPage;
    },
    isEditFlow() {
      return this.$context.flow === flowTypes.edit && this.$context.is.editPage;
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    minHosts() {
      if (this.isEditFlow && (this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice)) {
        return this.previousQuantity;
      }
      return DEFAULT_MIN_HOST;
    },
    maxHosts() {
      return DEFAULT_MAX_HOST;
    },
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    getUnit() {
      const units = {
        [BILLING_CYCLE.MONTHLY]: this.$t('billing.common.per_billing_cycle_license.monthly'),
        [BILLING_CYCLE.ANNUAL]: this.$t('billing.common.per_billing_cycle_license.annual'),
        [BILLING_CYCLE.QUARTERLY]: this.$t('billing.common.per_billing_cycle_license.quarterly'),
        [BILLING_CYCLE.TWO_YEARS]: this.$t('billing.common.per_billing_cycle_license.two_years'),
        [BILLING_CYCLE.THREE_YEARS]: this.$t('billing.common.per_billing_cycle_license.three_years'),
      }
      return (cycle) => units[cycle];
    },
    billingCycleOptions() {
      const { plans = [] } = this.getCurrentBasePlanInfo;
      const currentBasePlanCycleValue = get(plans, [0, 'cycleValue']);

      const basePlanName = this.$t(get(plans, [0, 'typeNameKey']));
      const addonPlanName = this.title;

      const shortestCyclePlan = _.sortBy(this.planList, p => p.cycleValue)[0];

      return this.planList.map(p => {
        const isDisabledExceedCycle = p.cycleValue > currentBasePlanCycleValue && get(this.currentEditPlan, ['billing_cycle'], '') !== p.billing_cycle;
        const isBulkLicenseAccountDisabled = this.isEditFlow && this.basicConfig.isBulkLicenseAccount && get(this.currentEditPlan, ['billing_cycle'], '') !== p.billing_cycle;
        const isDisabled = isDisabledExceedCycle || isBulkLicenseAccountDisabled;
        return Object.assign({}, p, {
          label: p.plan_code,
          cycle: this.$t(`billing.cycle.${p.billing_cycle}`),
          price: this.$money(p.amount, { forceDiff: true }),
          unit: this.getUnit(p.billing_cycle),
          disabled: isDisabled,
          //todo tooptip should match billing cycle
          disabledTooltip: isBulkLicenseAccountDisabled ? this.$t('billing.error.buld_acount_license') :
            isDisabledExceedCycle ? this.$t('billing.planform.annual_addon_disable_tooltip', [basePlanName, addonPlanName, basePlanName]) : '',
          cyclePrice: p.cycleValue >= BillingCycleEnum[BILLING_CYCLE.QUARTERLY] ? {
            annualPrice: (p.amount / p.cycleValue) * 12,
            monthlyPrice: shortestCyclePlan?.amount / shortestCyclePlan?.cycleValue,
          } : null
        });
      });
    },
    trackingPlanKey() {
      return get(trackingPlanTypes, get(planTypes, this.planType));
    },
    cartSelectedPlan() {
      return get(this.getSelectedPlans.filter((p) => p.type === this.planType), [0]);
    },
    decreaseTips() {
      const downSellData = this.basicConfig.downSellData;
      if (this.$context.is.edit && this.license === this.previousQuantity) {
        if (downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
  },
  mounted() {
    this.previousQuantity = this.currentEditPlan?.cartInfo?.selected_number ?? DEFAULT_MIN_HOST;
    // refresh page
    this.$nextTick(() => {
      this.mx_validDownSellLicense(this.license);
    });
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchSelectPlan']),
    onTipsLinkClick(e) {
      if (e.target.tagName.toLowerCase() === 'a') {
        this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`, 'view supported languages');
      }
    },
    onPlanSelect(newPlanCode) {
      const oldPlanCode = this.selectedPlanCode;

      if (this.selectedPlanCode !== newPlanCode) {
        if (oldPlanCode) {
          this.switchSelectPlan([oldPlanCode, newPlanCode, undefined]);
        } else {
          let quantity = this.license;
          if (quantity) {
            quantity = quantity <= this.maxHosts ? quantity : this.maxHosts;
          } else {
            quantity = this.minHosts;
          }
          this.license = quantity;
          this.updateSelectPlan({
            plan_code: newPlanCode,
            cartInfo: { selected_number: quantity },
          });
        }
        this.selectedPlanCode = newPlanCode;
        // event tracking
        const billingCycle = this.cartSelectedPlan?.billing_cycle;
        billingCycle && this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} billing cycle`,
          this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
        );
      }
    },
    changeLicense(newLicense) {
      const planCode = this.selectedPlanCode;
      const oldLicense = this.license;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newLicense },
        });
      }
      this.mx_validDownSellLicense(newLicense);

      this.license = newLicense;
      // event tracking
      planCode && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`,
        `${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
    },
    toggleLicenseTips(tips) {
      tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
    },
    // For A11Y
    getFirstFocusableControl() {
      return Promise.resolve(this.$refs.userInput)
    },
    collapseCard() {
      this.isExpanded = false;
    },
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? "";
        this.license = plan?.cartInfo?.selected_number ?? this.minHosts;
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} accordion`, val ? 'expand' : 'collapse');
      },
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/zoom-card.scss';

.opc-addon-config-row {
  &__input__number {
    margin-top: 24px;

    &--unit {
      margin-left: 8px;
    }
  }
}

.opc-addon-config {
  .opc-addon-config__main {
    border-top: 1px solid #dfe3e8;
    margin-top: 24px;

    &__title {
      margin-top: 24px;
    }
  }

  .zm-alert {
    padding: 8px 12px 8px 16px;
    margin: 14px 0;

    &--info {
      background: #F2F2F4;
    }

    &__body {
      padding: 0;
    }

    &__description {
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 150%;
      color: #515079;
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
